import { Fragment, useState, memo } from 'react';
import { StyledButton } from '../../theme/Styles';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import ClearIcon from '@mui/icons-material/Clear';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckIcon from '@mui/icons-material/Check';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    StyledCloseIcon,
} from './styles/DocumentsOfRequestsCardStyles';
import { DocumentViewer } from 'react-documents';
import { Dialog, CircularProgress } from '@mui/material';
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { downloadFile, downloadFileWithName } from '../../utilities/functions/DownloadUtil';
import { typesForDownload, typesForShow } from './DocumentsOfRequestsCardConstants';

function DocumentsOfRequestsCard({ 
    title, 
    data, 
    onSelectClick, 
    onDeleteClick, 
    hideSeeButton = false, 
    showDeleteButton = false, 
    showSelectButton = false, 
    disableCardStyle = false, 
    hideDownloadButton = false, 
    selectedItemsByIndex = [] 
}) {

    const [currentDocumentURL, setCurrentCurrentURL] = useState();
    const [viewerOpen, setViewerOpen] = useState(false);
    const [documentLoading, setDocumentLoading] = useState(false);

    const handleViewer = async ({ url, extension, name }) => {
        const _extension = extension === "vnd.ms-excel" ? "xls" :
            extension === "vnd.openxmlformats-officedocument.spreadsheetml.sheet" ?
                "xlsx" : extension
        if (url) {
            setViewerOpen(true);
            setDocumentLoading(true);
            try {
                if (_extension === 'pdf' || _extension === 'xls' || _extension === 'xlsx') {
                    const type = typesForDownload[_extension]
                    let downloadedDocument = await downloadFile(url, type);
                    setCurrentCurrentURL({ url: downloadedDocument, type: _extension, name: name });
                } else {
                    setCurrentCurrentURL({ url: url, type: _extension, name: name });
                }
            } catch (error) {
                console.error("Error downloading file:", error);
            } finally {
                setDocumentLoading(false);
            }
        } else {
            setCurrentCurrentURL(undefined);
            setViewerOpen(false);
            setDocumentLoading(false);
        }
    };

    const getExtension = (filename, documentType) => {
        if (documentType) return documentType.toLowerCase();
        return filename ? filename.split('.').pop().toLowerCase() : '';
    };

    const getBadgeInfo = (filename, documentType) => {
        const ext = getExtension(filename, documentType);
        
        if (ext === 'pdf') {
            return {
                className: 'pdf',
                icon: (
                    <svg viewBox="0 0 100 120" width="28" height="34">
                        {/* Page Outline */}
                        <path d="M 22 8 L 68 8 L 92 32 L 92 104 C 92 108.4 88.4 112 84 112 L 22 112 C 17.6 112 14 108.4 14 104 L 14 16 C 14 11.6 17.6 8 22 8 Z" 
                              fill="none" stroke="currentColor" strokeWidth="6" strokeLinejoin="round" />
                        
                        {/* Fold line */}
                        <path d="M 68 8 L 68 32 L 92 32" 
                              fill="none" stroke="currentColor" strokeWidth="6" strokeLinejoin="round" />
                              
                        {/* Adobe Acrobat Ribbon Logo (scaled and translated) */}
                        <g transform="translate(32.5, 36) scale(1.45)" fill="currentColor">
                            <path d="M13.9 16.4c-.6 0-1.1-.2-1.6-.5-1-.6-1.3-1.7-.8-2.6.5-.9 1.6-1.8 3.1-2.5 1.7-.8 3.8-1.5 5.3-2-.5-1-.9-2.1-1.3-3.1-.4-1.3-.5-2.5-.2-3.1.3-.6.9-1.1 1.7-1.1.4 0 .7.1 1 .3.9.6 1.2 1.6.9 2.8-.3 1.2-1.1 2.7-2.3 4.4 1.2.3 2.5.8 3.6 1.3 1.1.5 1.8 1.1 2.2 1.8.3.6.3 1.4-.2 1.9-.5.6-1.3.9-2.3.8-1.1-.1-2.4-.6-3.7-1.5-1.8.5-3.8 1.1-5.3 1.8-1.5.6-2.5 1.1-3.1 1.1zm3.2-9.4c.5 1 1 2 1.3 2.9.9-.3 1.9-.7 2.7-1.1.8-1.2 1.4-2.2 1.6-3 .1-.5 0-.9-.2-1.1-.1-.2-.3-.2-.5-.2-.4 0-.7.3-.9.9-.3.6-.7 1.5-1.1 2.5c-.3-.3-.6-.6-.9-1zm-3.6 7.9c.6 0 1.6-.5 2.9-1.1 1.4-.6 3.1-1.2 4.7-1.7-.9-.3-1.9-.6-2.9-.8-1.3.3-2.9.8-4.2 1.4-1.1.5-1.9 1-2.2 1.5-.2.4-.2.7.1.9.2.2.5.2.8.2.2.1.5.1.8.1z"/>
                        </g>
                        
                        {/* Text PDF at the bottom */}
                        <text x="53" y="98" className="pdf-icon-text" fontFamily="system-ui, -apple-system, sans-serif" fontWeight="900" fontSize="18" textAnchor="middle" letterSpacing="0.5">PDF</text>
                    </svg>
                )
            };
        }
        if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(ext)) {
            return {
                className: 'img',
                icon: (
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                        <path d="M19 19H5V5h10v4h4v10zM5 3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V8l-6-6H5zm8 9l-3 3.86L8 13l-3 4h14l-5-5z"/>
                    </svg>
                )
            };
        }
        if (['xls', 'xlsx', 'csv'].includes(ext)) {
            return {
                className: 'xls',
                icon: (
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 15H6v-5h12v5zm0-6H6V7h12v5z"/>
                    </svg>
                )
            };
        }
        return {
            className: 'doc',
            icon: (
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                    <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                </svg>
            )
        };
    };

    return (
        <div style={{ width: '100%' }}>
            {title && (
                <h4 style={{
                    margin: '0 0 16px 0',
                    fontSize: '15px',
                    fontWeight: 700,
                    color: '#475569',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em'
                }}>
                    {title}
                </h4>
            )}
            <div className="doc-card-list">
                {data?.map((item, index) => {
                    const thisItemIsPreviouslySelected = selectedItemsByIndex.includes(item.id);
                    const badge = getBadgeInfo(item.name, item.documentType);
                    const docExt = getExtension(item.name, item.documentType);

                    return (
                        <div key={index} className="doc-card-item">
                            <div className="doc-card-left">
                                <div className={`doc-card-badge ${badge.className}`}>
                                    {badge.icon}
                                </div>
                                <div className="doc-card-info">
                                    <p className="doc-card-name" title={item.name}>
                                        {item.name}
                                    </p>
                                    <p className="doc-card-date">
                                        📅 {item.date}
                                    </p>
                                </div>
                            </div>

                            <div className="doc-card-actions">
                                {typesForShow.includes(docExt) && !hideSeeButton && (
                                    <button 
                                        type="button"
                                        title="Ver documento"
                                        className="doc-action-btn"
                                        onClick={() => handleViewer({ url: item.url, extension: item.documentType, name: item.name })}
                                    >
                                        <VisibilityIcon style={{ fontSize: '18px', color: 'inherit' }} />
                                    </button>
                                )}

                                {!hideDownloadButton && (
                                    <button 
                                        type="button"
                                        title="Descargar"
                                        className="doc-action-btn"
                                        onClick={() => downloadFileWithName(item.url, item.name)}
                                    >
                                        <DownloadIcon style={{ fontSize: '18px', color: 'inherit' }} />
                                    </button>
                                )}

                                {showSelectButton && (
                                    <button 
                                        type="button"
                                        title={thisItemIsPreviouslySelected ? "Deseleccionar" : "Seleccionar documento"}
                                        className={`doc-action-btn select ${thisItemIsPreviouslySelected ? 'active' : ''}`}
                                        onClick={() => {
                                            if (thisItemIsPreviouslySelected) {
                                                onDeleteClick && onDeleteClick(item, index);
                                            } else {
                                                onSelectClick && onSelectClick(item, index);
                                            }
                                        }}
                                        style={{
                                            height: '36px',
                                            padding: '0 14px',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            borderRadius: '8px',
                                            fontSize: '12px',
                                            fontWeight: '700',
                                            fontFamily: 'Inter, sans-serif',
                                            cursor: 'pointer',
                                            border: thisItemIsPreviouslySelected ? '1px solid #192873' : '1px solid #CBD5E1',
                                            backgroundColor: thisItemIsPreviouslySelected ? '#192873' : '#F1F5F9',
                                            color: thisItemIsPreviouslySelected ? '#FFFFFF' : '#192873',
                                            transition: 'all 0.2s ease',
                                            boxShadow: 'none',
                                        }}
                                    >
                                        <CheckIcon style={{ fontSize: '16px', color: 'inherit' }} />
                                        <span>{thisItemIsPreviouslySelected ? 'Seleccionado' : 'Seleccionar'}</span>
                                    </button>
                                )}

                                {showDeleteButton && (
                                    <button 
                                        type="button"
                                        title="Eliminar"
                                        className="doc-action-btn delete"
                                        onClick={() => onDeleteClick && onDeleteClick(item, index)}
                                    >
                                        <ClearIcon style={{ fontSize: '18px', color: 'inherit' }} />
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <Dialog
                open={viewerOpen}
                onClose={handleViewer}
                onBackdropClick={handleViewer}
                fullWidth
                maxWidth="xl"
            >
                {/* Header (Shared for all formats) */}
                <div className="preview-modal-header">
                    <h3 className="preview-modal-title">
                        {currentDocumentURL?.name || 'Vista Previa de Documento'}
                    </h3>
                    <IconButton 
                        onClick={handleViewer}
                        sx={{ 
                            padding: '6px',
                            color: '#64748b',
                            '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.04)'
                            }
                        }}
                    >
                        <StyledCloseIcon style={{ fontSize: '20px' }} />
                    </IconButton>
                </div>

                {documentLoading ? (
                    /* Loading Spinner while file is downloading */
                    <div className="preview-canvas-container" style={{ minHeight: '400px' }}>
                        <CircularProgress color="secondary" />
                        <span style={{ fontSize: '15px', color: '#64748b', fontWeight: 500, fontFamily: 'Roboto', marginTop: '12px' }}>
                            Cargando documento...
                        </span>
                    </div>
                ) : (
                    /* Preview Content */
                    currentDocumentURL?.type === "pdf" ? (
                        <div style={{ height: '80vh', width: '100%', overflow: 'hidden' }}>
                            <DocumentViewer style={{ height: '100%', width: '100%' }} viewer="url" url={currentDocumentURL?.url} />
                        </div>
                    ) : (
                        /* Redesigned Image Viewer */
                        <div className="preview-canvas-container">
                            <TransformWrapper>
                                {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
                                    <Fragment>
                                        <TransformComponent wrapperStyle={{ alignSelf: 'center', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                                            <img 
                                                src={currentDocumentURL?.url} 
                                                className="preview-image" 
                                                alt="Document preview" 
                                            />
                                        </TransformComponent>

                                        {/* Floating Glassmorphism Toolbar */}
                                        <div className="preview-toolbar">
                                            <button 
                                                type="button"
                                                className="preview-tool-btn" 
                                                onClick={() => zoomIn()}
                                                title="Aumentar Zoom (+)"
                                            >
                                                <ZoomInIcon style={{ fontSize: '20px', color: 'inherit' }} />
                                            </button>
                                            <button 
                                                type="button"
                                                className="preview-tool-btn" 
                                                onClick={() => zoomOut()}
                                                title="Disminuir Zoom (-)"
                                            >
                                                <ZoomOutIcon style={{ fontSize: '20px', color: 'inherit' }} />
                                            </button>
                                            <button 
                                                type="button"
                                                className="preview-tool-btn" 
                                                onClick={() => resetTransform()}
                                                title="Restablecer"
                                            >
                                                <RefreshIcon style={{ fontSize: '20px', color: 'inherit' }} />
                                            </button>
                                            <div style={{ width: '1px', height: '20px', backgroundColor: '#cbd5e1', margin: '0 4px' }} />
                                            <button 
                                                type="button"
                                                className="preview-tool-btn" 
                                                onClick={() => downloadFileWithName(currentDocumentURL?.url, currentDocumentURL?.name)}
                                                title="Descargar Original"
                                            >
                                                <DownloadIcon style={{ fontSize: '20px', color: 'inherit' }} />
                                            </button>
                                        </div>
                                    </Fragment>
                                )}
                            </TransformWrapper>
                        </div>
                    )
                )}
            </Dialog>
        </div>
    );
}

export default memo(DocumentsOfRequestsCard);
